import { CSSResultGroup, html, PropertyValues, TemplateResult } from 'lit'
import { customElement, query, state } from 'lit/decorators.js'
import { CouldNotCreatePaymentPayload } from 'components/ordering/checkout/payment-methods/payment-backend/start-payment-api.js'
import { paymentMethodSelectable } from 'components/ordering/checkout/payment-methods/payment-methods.style.js'
import {
  CSCMolliePaymentMethod,
  PaymentMethodState
} from 'components/ordering/checkout/payment-methods/methods/payment-method.js'
import {
  TokenCreationError
} from 'components/ordering/checkout/payment-methods/methods/mollie/third-party/mollie-response-parser.js'
import {
  THIRD_PARTY_MOLLIE_API,
  ThirdPartyMollieApi
} from 'components/ordering/checkout/payment-methods/methods/mollie/third-party/third-party-mollie-api.js'
import { mollieCreditCardStyle } from './mollie-credit-card.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { MollieCreditCardPayload } from '@cloudsuite/cs-payment-defs/dist/index.js'


export interface TrydentMollieCreditCardData {
  id: string
  description: string
  image_url: string
  profile_id: string
  locale: string
  test_mode: boolean
}


@customElement('csc-mollie-creditcard')
export class CSCMollieCreditCard extends CSCMolliePaymentMethod  {
  static override styles: CSSResultGroup = [hostStyle, paymentMethodSelectable, mollieCreditCardStyle]

  @inject(THIRD_PARTY_MOLLIE_API)
  thirdPartyMollieAPI!: ThirdPartyMollieApi

  @query('#mollie-card-mount')
  private mollieCardMount!: HTMLElement

  @state() protected readonly paymentMethodId: string
  @state() protected readonly customDescription: string | null
  @state() protected state: PaymentMethodState

  @state() private readonly description: string
  @state() private readonly imageUrl: string
  @state() private readonly profileId: string
  @state() private readonly locale: string
  @state() private readonly testMode: boolean


  constructor({ id, description, image_url, profile_id, locale, test_mode }: TrydentMollieCreditCardData, state: PaymentMethodState, customDescription: string | null) {
    super()

    this.paymentMethodId = id
    this.customDescription = customDescription
    this.state = state

    this.description = description
    this.imageUrl = image_url
    this.profileId = profile_id
    this.locale = locale
    this.testMode = test_mode
  }

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties)
    if (this.state === 'selected') {
      this.thirdPartyMollieAPI.initialize(this.profileId, this.locale, this.testMode, this.mollieCardMount)
    }
  }

  private renderCreditCardMount(): TemplateResult {
    return html`
      <div class="csc-mollie-creditcard">
        <div id="mollie-card-mount"></div>
      </div>
    `
  }

  protected renderPaymentMethodContent = (): TemplateResult => {
    return html`
      <div class="selectable-content">
        <cse-image
          source="${this.imageUrl}"
          alt="${this.description}"
          width="55"
          height="41"
        >
        </cse-image>
        <cse-text>${this.description}</cse-text>
      </div>
      ${this.state === 'selected' ? this.renderCreditCardMount() : null}
    `
  }

  protected getPayload = async (): Promise<MollieCreditCardPayload | CouldNotCreatePaymentPayload> => {
    const token = await this.thirdPartyMollieAPI.createToken()

    if (token instanceof TokenCreationError) { return new CouldNotCreatePaymentPayload() }

    return new MollieCreditCardPayload(token)
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'csc-mollie-creditcard': CSCMollieCreditCard,
  }
}
